import { useSyncExternalStore } from "react";

// One question at a time for the promise modal: a pre-order, a gathered card or a set is a
// promise the member confirms, and removing one is final, so both ask first. Kept outside React
// so any button can ask and wait for the answer; <PromiseDialogHost /> on the page shows it.

export interface PromiseAsk {
  title: string;
  body: string[];
  /** The big button. */
  confirm: string;
  /** The small way out. */
  dismiss: string;
  /** A removal that can't be undone: the warning look, and the way out gets the focus. */
  final?: boolean;
}

type Pending = { ask: PromiseAsk; resolve: (ok: boolean) => void };

let current: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Shows the modal and resolves true when the member confirms, false otherwise. */
export function askPromise(ask: PromiseAsk): Promise<boolean> {
  // a second question replaces an unanswered one
  current?.resolve(false);
  return new Promise((resolve) => {
    current = { ask, resolve };
    emit();
  });
}

export function settlePromise(ok: boolean) {
  const pending = current;
  if (!pending) return;
  current = null;
  emit();
  pending.resolve(ok);
}

export function usePromiseAsk(): PromiseAsk | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => current?.ask ?? null,
    () => null,
  );
}
